import React, { useEffect } from 'react';
import { Mic, BriefcaseBusiness, Coffee, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import NotificationTestButton from '@/components/NotificationTestButton';
import { useLabsSettings } from '@/modules/labs/hooks/useLabsSettings';
import { getTTSAndNotificationPrefs, speakTTS } from '@/modules/labs/utils/tts';

const DAY_LABELS = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];

function ToggleRow({
  name,
  label,
  description,
  checked,
  onChange,
}: {
  name: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <label htmlFor={name} className="flex-1 cursor-pointer">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {description && <p className="text-xs text-slate-500">{description}</p>}
      </label>
      <label className="relative ml-3 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center">
        <input
          id={name}
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
        />
        <span className="block h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-slate-900" />
        <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
      </label>
    </div>
  );
}

function DayPicker({
  days,
  onChange,
}: {
  days: number[];
  onChange: (days: number[]) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {DAY_LABELS.map((label, i) => {
        const day = i + 1;
        const active = days.includes(day);
        return (
          <button
            key={day}
            type="button"
            onClick={() =>
              onChange(active ? days.filter(d => d !== day) : [...days, day])
            }
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors',
              active
                ? 'bg-slate-900 text-white'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function ReminderSection({
  title,
  icon,
  enabled,
  onToggleName,
  hour,
  days,
  text,
  notifTitle,
  notifBody,
  notifButtonLabel,
  onToggle,
  onHourChange,
  onDaysChange,
  onTextChange,
}: {
  title: string;
  icon: React.ReactNode;
  enabled: boolean;
  onToggleName: string;
  hour: string;
  days: number[];
  text: string;
  notifTitle: string;
  notifBody: string;
  notifButtonLabel: string;
  onToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onHourChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDaysChange: (days: number[]) => void;
  onTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center gap-3 px-5 pb-3 pt-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
          {icon}
        </div>
        <h2 className="flex-1 text-sm font-semibold text-slate-900">{title}</h2>
        <label className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center">
          <input
            id={onToggleName}
            type="checkbox"
            className="sr-only peer"
            checked={enabled}
            onChange={onToggle}
          />
          <span className="block h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-slate-900" />
          <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
        </label>
      </div>

      {enabled && (
        <div className="space-y-4 px-5 pb-5">
          <Separator />
          <div className="flex items-center gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Horário</label>
              <input
                type="time"
                value={hour}
                onChange={onHourChange}
                step={60}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
              />
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-medium text-slate-500">Dias da semana</p>
            <DayPicker days={days} onChange={onDaysChange} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-500">
              Texto do lembrete
            </label>
            <input
              type="text"
              value={text}
              onChange={onTextChange}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
            />
          </div>
          <NotificationTestButton
            title={notifTitle}
            body={notifBody}
            actions={[{ action: 'ok', title: 'OK' }, { action: 'snooze', title: 'Adiar' }]}
            buttonLabel={notifButtonLabel}
          />
        </div>
      )}
    </div>
  );
}

const NotificationsPage: React.FC = () => {
  useEffect(() => { document.title = 'Notificações'; }, []);
  const { settings, setSettings, handleToggle, handleText } = useLabsSettings();

  const testTTS = () => {
    const prefs = getTTSAndNotificationPrefs();
    speakTTS('Teste de TTS. Funcionando!', prefs.language);
  };

  return (
    <main className="space-y-4 px-4 py-6" id="main-content" role="main" aria-label="Notificações">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Notificações</h1>
        <p className="mt-1 text-sm text-slate-500">
          Configure lembretes e voz para suas marcações.
        </p>
      </div>

      {/* TTS */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-3 px-5 pb-3 pt-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Mic className="h-4 w-4" />
          </div>
          <h2 className="flex-1 text-sm font-semibold text-slate-900">Text-to-Speech (TTS)</h2>
          <label className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center">
            <input
              id="use-tts"
              type="checkbox"
              className="sr-only peer"
              checked={!!settings.useTTS}
              onChange={handleToggle('useTTS')}
            />
            <span className="block h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-slate-900" />
            <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
          </label>
        </div>
        {settings.useTTS && (
          <div className="space-y-4 px-5 pb-5">
            <Separator />
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-500">
                Texto para check-in
              </label>
              <input
                type="text"
                value={settings.ttsCheckIn}
                onChange={handleText('ttsCheckIn')}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-500">
                Texto para check-out
              </label>
              <input
                type="text"
                value={settings.ttsCheckOut}
                onChange={handleText('ttsCheckOut')}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
              />
            </div>
            <Button variant="outline" size="sm" onClick={testTTS} aria-label="Testar TTS" className="gap-2">
              <Mic className="h-4 w-4" aria-hidden="true" />
              Testar TTS
            </Button>
          </div>
        )}
      </div>

      {/* Reminders */}
      <ReminderSection
        title="Lembrar para trabalhar"
        icon={<BriefcaseBusiness className="h-4 w-4" />}
        enabled={settings.remindToWork}
        onToggleName="remind-work"
        hour={settings.remindToWorkHour}
        days={settings.remindToWorkDays}
        text={settings.remindToWorkText}
        notifTitle={settings.remindToWorkText || 'Hora de trabalhar!'}
        notifBody={`Lembrete de trabalho para ${settings.remindToWorkHour}`}
        notifButtonLabel="Testar notificação de trabalho"
        onToggle={handleToggle('remindToWork')}
        onHourChange={e => setSettings(s => ({ ...s, remindToWorkHour: e.target.value }))}
        onDaysChange={days => setSettings(s => ({ ...s, remindToWorkDays: days }))}
        onTextChange={e => setSettings(s => ({ ...s, remindToWorkText: e.target.value }))}
      />

      <ReminderSection
        title="Lembrar para descansar"
        icon={<Coffee className="h-4 w-4" />}
        enabled={settings.remindToRest}
        onToggleName="remind-rest"
        hour={settings.remindToRestHour}
        days={settings.remindToRestDays}
        text={settings.remindToRestText}
        notifTitle={settings.remindToRestText || 'Hora de descansar!'}
        notifBody={`Lembrete de descanso para ${settings.remindToRestHour}`}
        notifButtonLabel="Testar notificação de descanso"
        onToggle={handleToggle('remindToRest')}
        onHourChange={e => setSettings(s => ({ ...s, remindToRestHour: e.target.value }))}
        onDaysChange={days => setSettings(s => ({ ...s, remindToRestDays: days }))}
        onTextChange={e => setSettings(s => ({ ...s, remindToRestText: e.target.value }))}
      />

      <ReminderSection
        title="Lembrar para ir embora"
        icon={<LogOut className="h-4 w-4" />}
        enabled={settings.remindToLeave}
        onToggleName="remind-leave"
        hour={settings.remindToLeaveHour}
        days={settings.remindToLeaveDays}
        text={settings.remindToLeaveText}
        notifTitle={settings.remindToLeaveText || 'Hora de ir embora!'}
        notifBody={`Lembrete de saída para ${settings.remindToLeaveHour}`}
        notifButtonLabel="Testar notificação de saída"
        onToggle={handleToggle('remindToLeave')}
        onHourChange={e => setSettings(s => ({ ...s, remindToLeaveHour: e.target.value }))}
        onDaysChange={days => setSettings(s => ({ ...s, remindToLeaveDays: days }))}
        onTextChange={e => setSettings(s => ({ ...s, remindToLeaveText: e.target.value }))}
      />

      <div>
        <ToggleRow
          name="notif-placeholder"
          label="Notificações do sistema"
          description="Em breve"
          checked={false}
          onChange={() => {}}
        />
      </div>
    </main>
  );
};

export default NotificationsPage;
